import { useState } from "react";
import { Linkedin, Send } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import LogoIcon from "./LogoIcon";
import { useLanguage } from "../i18n/LanguageContext";

const XIcon = ({ size = 14, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

function NewsletterForm() {
  const { t } = useLanguage();
  const n = t.newsletter;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        setStatus("error");
        return;
      }
      const data = await res.json();
      if (data.message && data.message.toLowerCase().includes("already")) {
        setStatus("exists");
      } else {
        setStatus("success");
        setEmail("");
      }
    } catch (err) {
      setStatus("error");
    }
    setTimeout(() => setStatus("idle"), 4000);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="footer-newsletter" className="mt-6">
      <div className="flex">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={n.placeholder}
          required
          data-testid="footer-newsletter-email"
          className="input-glow flex-1 bg-[#0F1A2E] border border-slate-700 text-slate-200 text-xs px-3 py-2.5 focus:border-[#D4AF37] focus:outline-none placeholder-slate-600 transition-all duration-300 min-w-0"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="footer-newsletter-submit"
          className="btn-gold bg-[#D4AF37] text-[#0B1221] px-3 py-2.5 hover:bg-[#F59E0B] disabled:opacity-60 flex-shrink-0"
        >
          <Send size={12} />
        </button>
      </div>
      {status === "success" && <p className="text-[#D4AF37] text-xs mt-2">{n.success}</p>}
      {status === "exists" && <p className="text-slate-400 text-xs mt-2">{n.alreadySubscribed}</p>}
      {status === "error" && <p className="text-red-400 text-xs mt-2">{n.error}</p>}
    </form>
  );
}

export default function Footer() {
  const { lang, t } = useLanguage();
  const f = t.footer;
  const n = t.newsletter;
  const location = useLocation();
  const isHome = location.pathname === "/";

  const scrollTo = (id) => {
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#1E3455] border-t border-slate-700/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <LogoIcon className="h-12" />
              <span className="text-white font-semibold tracking-widest text-sm uppercase">
                Advaltis
              </span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed max-w-xs">{f.desc}</p>
            <div className="flex gap-3 mt-6">
              <a href="https://www.linkedin.com/company/advaltis" target="_blank" rel="noreferrer" aria-label="LinkedIn"
                className="social-icon-hover w-9 h-9 border border-slate-700 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center transition-colors duration-300 group">
                <Linkedin size={14} className="text-slate-300 group-hover:text-[#D4AF37] transition-colors" />
              </a>
              <a href="https://x.com/advaltis" target="_blank" rel="noreferrer" aria-label="X (Twitter)"
                className="social-icon-hover w-9 h-9 border border-slate-700 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center justify-center transition-colors duration-300 group">
                <XIcon size={14} className="text-slate-300 group-hover:text-[#D4AF37] transition-colors" />
              </a>
            </div>
          </div>
          <div>
            <h4 className="text-slate-300 text-[10px] uppercase tracking-widest mb-6">{f.navLabel}</h4>
            <ul className="space-y-3">
              {t.nav.links.map((link) => (
                <li key={link.id}>
                  <button onClick={() => scrollTo(link.id)} className="text-slate-300 hover:text-[#D4AF37] text-sm transition-all duration-200 hover:translate-x-1 inline-block">{link.label}</button>
                </li>
              ))}
              <li>
                <Link to="/blog" className="text-slate-300 hover:text-[#D4AF37] text-sm transition-all duration-200 hover:translate-x-1 inline-block">Blog</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-slate-300 text-[10px] uppercase tracking-widest mb-6">{f.contactLabel}</h4>
            <div className="space-y-4">
              <p className="text-slate-300 text-sm leading-relaxed">{f.address[0]}<br />{f.address[1]}</p>
              <a href="mailto:info@advaltis.com" className="text-slate-300 hover:text-[#D4AF37] text-sm transition-colors block">info@advaltis.com</a>
              <a href="tel:+447717536635" className="text-slate-300 hover:text-[#D4AF37] text-sm transition-colors block">+44 7717 536635</a>
            </div>
          </div>
          <div>
            <h4 className="text-slate-300 text-[10px] uppercase tracking-widest mb-6">{n.heading}</h4>
            <p className="text-slate-400 text-xs leading-relaxed mb-1">{n.desc}</p>
            <NewsletterForm />
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-xs">&copy; {new Date().getFullYear()} {f.copyright}</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-500 text-xs hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy">{lang === "fr" ? "Politique de confidentialit\u00e9" : "Privacy Policy"}</Link>
            <Link to="/terms" className="text-slate-500 text-xs hover:text-[#D4AF37] transition-colors" data-testid="footer-terms">{lang === "fr" ? "Conditions d\u2019utilisation" : "Terms of Use"}</Link>
          </div>
          <p className="text-slate-500 text-xs">{f.location}</p>
        </div>
      </div>
    </footer>
  );
}
